import { useEffect, useReducer, useCallback, useRef } from "react";
import { ReactDataContext } from "@themost/react";
import { getCookie } from "components/Common/CommonFunctions";
import { reducer } from '../reducers/evaluationFiltersReducer'
import {
    SET_PAGE_ACTION,
    SET_TOTAL_ACTION,
    UPDATE_MULTIPLE_VALUES_ACTION,
    UPDATE_STUDIES_ACTION
} from "../reducers/evaluationQueryReducer";


export const defaultState = {
    evaluation: [],
    page: 0,
    total: 0,
    loading: true,
    departments: [],
    countries: [],
    applicationYears: [],
    iiaDepartments: [],
    facultyDepartmentId: null,
    facultyDepartmentName: null,
    departmentalCoordinators: []
}

const getCurrentRole = () => {
    try {
        return JSON.parse(localStorage.getItem("role"));
    } catch (err) {
        return null;
    }
}

const getUserEmailFromCookie = () => {
    try {
        const userCookie = getCookie("user");
        return userCookie ? JSON.parse(userCookie)?.email : null;
    } catch (err) {
        return null;
    }
}

const getDepartmentId = (department) => {
    if (department === null || department === undefined) {
        return null;
    }
    return typeof department === "object" ? department?.id : department;
}

export const useEvaluationQuery = (filters, userService, langCode) => {

    const [state, dispatch] = useReducer(reducer, defaultState)

    const context = new ReactDataContext(process.env.REACT_APP_API_SERVER);
    context.setBearerAuthorization(userService.getToken());

    const mountedRestOptions = useRef(false);

    const setPage = useCallback((page) => {
        dispatch({ type: SET_PAGE_ACTION, payload: { page } })
    }, [])

    const updateMultipleValues = useCallback((data) => {
        dispatch({ type: UPDATE_MULTIPLE_VALUES_ACTION, payload: data })
    }, []);

    const updateEvaluation = useCallback((data) => {
        dispatch({ type: UPDATE_STUDIES_ACTION, payload: data })
    }, [])

    const setTotal = useCallback((data) => {
        dispatch({ type: SET_TOTAL_ACTION, payload: data })
    }, [])

    const hasMandatoryFilters = useCallback(() => {
        return Boolean(filters.state.departmentSelected && filters.state.dateSelected);
    }, [filters.state.departmentSelected, filters.state.dateSelected]);

    const fetchEvaluation = async (pageNumber) => {
        if (!hasMandatoryFilters()) {
            updateMultipleValues({
                evaluation: [],
                loading: false,
                total: 0,
                page: 0
            });
            return;
        }

        let fields = [];
        if (filters.state.idSelected) {
            fields.push({
                "key": "applicationNumber",
                "value": filters.state.idSelected
            })
        }

        if (getCurrentRole()?.role === "Faculty" && !state.facultyDepartmentId) {
            updateMultipleValues({
                evaluation: [],
                loading: false,
                total: 0,
                page: pageNumber
            });
            return;
        }

        // Build base query with common filters
        let query = context.model(`ApplicationStudentStudiesForms`).asQueryable()
            .where("id").greaterThan(0)
            .and("isDeleted").equal(false)
            .and("status").equal(["validated", "awaiting_placement"]);

        if (filters.state.dateSelected) {
            query = query.and('applicationYear').equal(filters.state.dateSelected)
        }

        if (filters.state.departmentSelected) {
            const departmentQueryField = langCode === "gr" ? "studiesData/department/nameGR" : "studiesData/department/name";
            query = query.and(departmentQueryField).equal(filters.state.departmentSelected)
        }

        if (filters.state.universitySelected) {
            query = query.and("studiesData/department/institution/name").equal(filters.state.universitySelected)
        }

        if (filters.state.erasmusCodeSelected) {
            query = query.and("studiesData/department/institution/erasmusCode").equal(filters.state.erasmusCodeSelected)
        }

        if (filters.state.countrySelected) {
            query = query.and("studiesData/department/institution/country").equal(filters.state.countrySelected)
        }

        if (filters.state.departmentalCoordinatorSelected) {
            query = query.and("prefferedMobility/iiaId/departmentalCoordinatorContacts/id").equal(filters.state.departmentalCoordinatorSelected)
        }

        try {
            const studentEvaluationResults = await query.getItems();

            // For Faculty users: Filter to show applications where either:
            // 1. Student is from faculty's department (studiesData match)
            // 2. Faculty's department is a coordinator on the mobility (IIA coordinator match)
            const filteredByFacultyDepartment = state.facultyDepartmentId
                ? studentEvaluationResults.filter((form) => {
                    const matchesStudies = (form?.studiesData || []).some(
                        study => study?.department?.id === state.facultyDepartmentId
                    );

                    const matchesMobility = (form?.prefferedMobility || []).some(
                        mobility => (mobility?.iiaId?.departmentalCoordinatorContacts || []).some(
                            contact => contact?.department?.id === state.facultyDepartmentId
                        )
                    );

                    return matchesStudies || matchesMobility;
                })
                : studentEvaluationResults;

            const normalizedSelectedCoordinatorId = filters.state.departmentalCoordinatorSelected ? String(filters.state.departmentalCoordinatorSelected) : null;
            const filteredBySecondCoordinator = normalizedSelectedCoordinatorId
                ? filteredByFacultyDepartment.filter((application) =>
                    (application?.prefferedMobility || []).some((mobility) =>
                        String(mobility?.iiaId?.departmentalCoordinatorContacts?.[1]?.id) === normalizedSelectedCoordinatorId
                    )
                )
                : filteredByFacultyDepartment;

            updateMultipleValues({
                evaluation: filteredBySecondCoordinator,
                loading: false,
                total: filteredBySecondCoordinator.length,
                page: pageNumber
            })
        } catch (err) {
            console.log(err);
        }

    }

    //runs on first mount
    useEffect(async() => {
        let departments, countries, departmentalCoordinators, facultyContact, applicationYears, iiaDepartments;
        const currentRole = getCurrentRole();
        const userEmail = getUserEmailFromCookie();
        try {
            [departments, countries, departmentalCoordinators, applicationYears] = await Promise.all([
                context.model('MobilityDepartments').asQueryable().where('institution').equal(1).take(-1).getItems(),
                context.model('MobilityCountries').asQueryable().take(-1).getItems(),
                context.model('MobilityContacts').asQueryable().where('department/institution/id').equal(1).take(-1).getItems(),
                context.model('ApplicationStudentStudiesForms')
                    .asQueryable()
                    .select('applicationYear')
                    .where('id').greaterThan(0)
                    .and('isDeleted').equal(false)
                    .and('status').equal('submitted')
                    .orderBy('applicationYear desc')
                    .take(-1)
                    .getItems()
            ]);
            if (currentRole?.role === "Faculty" && userEmail) {
                facultyContact = await context.model(`MobilityContacts`).asQueryable().where("email").equal(userEmail).getItem();
                const facultyDeptId = getDepartmentId(facultyContact?.department);
                const facultyDeptName = langCode === "gr" ? (facultyContact?.department?.nameGR || facultyContact?.department?.name || "") : (facultyContact?.department?.name || "");
                if (facultyDeptId) {
                    let iiaDeptQuery = context.model('ApplicationStudentStudiesForms')
                        .asQueryable()
                        .where('id').greaterThan(0)
                        .and('isDeleted').equal(false)
                        .and('status').equal('submitted');
                    
                    // Apply mandatory department filter (same as fetchEvaluation)
                    if (facultyDeptName) {
                        const departmentQueryField = langCode === "gr" ? "studiesData/department/nameGR" : "studiesData/department/name";
                        iiaDeptQuery = iiaDeptQuery.and(departmentQueryField).equal(facultyDeptName);
                    }
                    
                    iiaDepartments = await iiaDeptQuery.take(-1).getItems();
                }
            }
        } catch (err) {
            console.log(err);
        }
        const distinctYears = [...new Set(applicationYears?.map(r => r.applicationYear))];
        // Extract unique IIA departments that are co-coordinators on IIAs where faculty's department is also a coordinator
        // Only include departments from applications that pass the faculty filter (same logic as fetchEvaluation)
        const distinctIiaDepartments = iiaDepartments
            ? [...new Map(
                iiaDepartments
                    .filter(app => {
                        // Apply same faculty filter as fetchEvaluation
                        const matchesStudies = (app?.studiesData || []).some(
                            study => study?.department?.id === getDepartmentId(facultyContact?.department)
                        );
                        const matchesMobility = (app?.prefferedMobility || []).some(
                            mobility => (mobility?.iiaId?.departmentalCoordinatorContacts || []).some(
                                contact => contact?.department?.id === getDepartmentId(facultyContact?.department)
                            )
                        );
                        return matchesStudies || matchesMobility;
                    })
                    .flatMap(app => (app.prefferedMobility || []))
                    .filter(mobility => {
                        // Only include mobilities where faculty's department is a coordinator
                        return (mobility?.iiaId?.departmentalCoordinatorContacts || []).some(
                            contact => contact?.department?.id === getDepartmentId(facultyContact?.department)
                        );
                    })
                    .flatMap(mobility => {
                        // Extract OTHER coordinator departments (exclude faculty's own dept)
                        return (mobility?.iiaId?.departmentalCoordinatorContacts || [])
                            .map(contact => contact?.department)
                            .filter(dept => dept && dept.id !== getDepartmentId(facultyContact?.department));
                    })
                    .map(dept => [dept.id, dept])
            ).values()]
            : [];
        updateMultipleValues({
            departments: departments,
            countries: countries,
            applicationYears: distinctYears,
            iiaDepartments: distinctIiaDepartments,
            loading: false,
            facultyDepartmentId: getDepartmentId(facultyContact?.department),
            facultyDepartmentName: langCode === "gr" ? (facultyContact?.department?.nameGR || facultyContact?.department?.name || "") : (facultyContact?.department?.name || ""),
            departmentalCoordinators: (departmentalCoordinators || []).map((contact) => ({
                ...contact,
                label: `${contact?.familyName || ""} ${contact?.givenName || ""}`.trim()
            }))
        })
    }, [langCode])

    // runs when the user changes the rest of the options
    useEffect(() => {
        if (mountedRestOptions.current === false) {
            mountedRestOptions.current = true;
            return;
        }

        (async () => {
            if (!hasMandatoryFilters()) {
                updateMultipleValues({
                    evaluation: [],
                    loading: false,
                    total: 0,
                    page: 0
                });
                return;
            }
            fetchEvaluation(0);
        })()
        // fetchEvaluation(parentState.currentPage);
    }, [
        filters.state.idSelected,
        filters.state.departments,
        filters.state.typeSelected,
        filters.state.dateSelected,
        filters.state.departmentSelected,
        filters.state.universitySelected,
        filters.state.erasmusCodeSelected,
        filters.state.countrySelected,
        filters.state.departmentalCoordinatorSelected,
        filters.state.iiaDepartmentSelected,
        state.facultyDepartmentId,
        hasMandatoryFilters
    ])

    return {
        evaluation: state.evaluation,
        loading: state.loading,
        page: state.page,
        total: state.total,
        departments: state.departments,
        countries: state.countries,
        applicationYears: state.applicationYears,
        iiaDepartments: state.iiaDepartments,
        departmentalCoordinators: state.departmentalCoordinators,
        facultyDepartmentId: state.facultyDepartmentId,
        facultyDepartmentName: state.facultyDepartmentName,
        setPage,
        updateEvaluation,
        setTotal
    }
}
